import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowDownToLine, History, Wallet, Clock, TrendingUp, Banknote, Landmark } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { StatCard, Spinner, PageHeader, StatusBadge, EmptyState } from "@/components/common";
import { api, naira, fmtDate, fmtDateTime, errMsg, media } from "@/lib/api";

const TX_LABEL = { sale: "Template Sale", platform_fee: "Platform Fee", withdrawal: "Withdrawal", purchase: "Purchase" };

export function TxTable({ items, showUser }) {
  if (!items.length) return <EmptyState title="No transactions yet" testid="tx-empty" />;
  return (
    <div className="card-surface overflow-x-auto">
      <Table>
        <TableHeader><TableRow><TableHead>Date</TableHead>{showUser && <TableHead>User</TableHead>}<TableHead>Type</TableHead><TableHead>Description</TableHead><TableHead className="text-right">Amount</TableHead><TableHead>Status</TableHead><TableHead>Transaction ID</TableHead></TableRow></TableHeader>
        <TableBody>
          {items.map((t) => (
            <TableRow key={t.id} data-testid={`tx-row-${t.id}`}>
              <TableCell className="whitespace-nowrap text-xs">{fmtDateTime(t.created_at)}</TableCell>
              {showUser && <TableCell className="text-xs">{t.user_name}</TableCell>}
              <TableCell className="whitespace-nowrap font-medium">{TX_LABEL[t.type] || t.type}</TableCell>
              <TableCell className="min-w-[200px] text-xs text-muted-foreground">{t.description}</TableCell>
              <TableCell className={`whitespace-nowrap text-right font-semibold ${t.amount >= 0 ? "text-emerald-500" : "text-rose-500"}`}>{t.amount >= 0 ? "+" : "-"}{naira(Math.abs(t.amount))}</TableCell>
              <TableCell><StatusBadge s={t.status} /></TableCell>
              <TableCell className="font-mono-ui text-[11px] text-muted-foreground">{t.tx_ref}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export function SellerWallet() {
  const [d, setD] = useState(null);
  const [wds, setWds] = useState([]);
  useEffect(() => { api.get("/seller/wallet").then((r) => setD(r.data)); api.get("/seller/withdrawals").then((r) => setWds(r.data)); }, []);
  if (!d) return <Spinner />;
  const w = d.wallet;
  return (
    <div>
      <PageHeader eyebrow="Seller wallet" title="Wallet">
        <div className="flex gap-2">
          <Button asChild className="rounded-full" data-testid="wallet-withdraw-btn"><Link to="/seller/withdraw"><ArrowDownToLine size={16} className="mr-1.5" />Withdraw Money</Link></Button>
          <Button asChild variant="outline" className="rounded-full" data-testid="wallet-history-btn"><Link to="/seller/transactions"><History size={16} className="mr-1.5" />Transaction History</Link></Button>
        </div>
      </PageHeader>
      <div className="grain relative overflow-hidden rounded-3xl bg-primary p-7 text-white">
        <p className="font-mono-ui text-xs uppercase tracking-[0.2em] text-white/70">Available balance</p>
        <p className="mt-2 font-display text-4xl font-bold sm:text-5xl" data-testid="wallet-available">{naira(w.available_balance)}</p>
        <p className="mt-3 text-xs text-white/70">Sales settle into your available balance {d.settlement_days} day(s) after purchase.</p>
      </div>
      <div className="mt-4 grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        <StatCard testid="wallet-pending" label="Pending" value={naira(w.pending_balance)} icon={Clock} accent="text-amber-500" />
        <StatCard testid="wallet-total-earnings" label="Total earnings" value={naira(w.total_earnings)} icon={TrendingUp} accent="text-emerald-500" />
        <StatCard testid="wallet-total-withdrawn" label="Total withdrawn" value={naira(w.total_withdrawn)} icon={Banknote} />
        <StatCard testid="wallet-in-review" label="In withdrawal" value={naira(w.locked_balance)} icon={Wallet} />
      </div>
      <h2 className="mb-4 mt-10 text-xl font-bold">Withdrawal requests</h2>
      {wds.length === 0 ? <p className="text-sm text-muted-foreground">No withdrawals yet.</p> : (
        <div className="card-surface divide-y divide-border">
          {wds.map((x) => (
            <div key={x.id} className="flex flex-wrap items-center gap-3 p-4 text-sm" data-testid={`withdrawal-${x.id}`}>
              <Landmark size={18} className="text-muted-foreground" />
              <div className="min-w-0 flex-1"><p className="font-medium">{naira(x.amount)} → {x.bank_name} ••••{x.account_number.slice(-4)}</p><p className="font-mono-ui text-xs text-muted-foreground">{x.reference} · {fmtDate(x.created_at)}{x.admin_note ? ` · ${x.admin_note}` : ""}</p></div>
              <StatusBadge s={x.status} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function Withdraw() {
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [f, setF] = useState({ bank_name: "", account_name: "", account_number: "", amount: "" });
  const [confirm, setConfirm] = useState(false);
  useEffect(() => { api.get("/seller/wallet").then((r) => { setD(r.data); if (r.data.saved_bank) setF((x) => ({ ...x, ...r.data.saved_bank })); }); }, []);
  if (!d) return <Spinner />;
  const amt = Number(f.amount || 0);
  const receive = Math.max(0, amt - d.withdrawal_fee);
  const open = (e) => {
    e.preventDefault();
    if (!/^\d{10}$/.test(f.account_number)) return toast.error("Account number must be 10 digits");
    if (amt < d.min_withdrawal) return toast.error(`Minimum withdrawal is ${naira(d.min_withdrawal)}`);
    if (amt > d.wallet.available_balance) return toast.error("Amount exceeds available balance");
    setConfirm(true);
  };
  const submit = async () => {
    try { await api.post("/seller/withdrawals", { ...f, amount: amt }); toast.success("Withdrawal request submitted for review"); nav("/seller/wallet"); } catch (e) { toast.error(errMsg(e)); }
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <div className="max-w-2xl">
      <PageHeader eyebrow="Payouts" title="Withdraw Money" />
      <form onSubmit={open} className="card-surface space-y-5 p-6">
        <div className="flex items-center justify-between rounded-xl bg-secondary p-4"><span className="text-sm text-muted-foreground">Available balance</span><span className="font-display text-xl font-bold" data-testid="withdraw-available">{naira(d.wallet.available_balance)}</span></div>
        <div className="space-y-2"><Label>Method</Label><div className="flex items-center gap-2 rounded-xl border border-primary bg-primary/5 p-3 text-sm font-medium"><Landmark size={16} className="text-primary" />Bank Transfer (NGN)</div></div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2"><Label>Account name</Label><Input data-testid="withdraw-account-name-input" required value={f.account_name} onChange={set("account_name")} /></div>
          <div className="space-y-2"><Label>Bank</Label><Input data-testid="withdraw-bank-input" required value={f.bank_name} onChange={set("bank_name")} placeholder="e.g. GTBank" /></div>
          <div className="space-y-2"><Label>Account number</Label><Input data-testid="withdraw-account-number-input" required inputMode="numeric" maxLength={10} value={f.account_number} onChange={set("account_number")} /></div>
          <div className="space-y-2"><Label>Amount (₦)</Label><Input data-testid="withdraw-amount-input" required type="number" value={f.amount} onChange={set("amount")} /></div>
        </div>
        <div className="space-y-2 border-t border-border pt-4 text-sm">
          <div className="flex justify-between"><span className="text-muted-foreground">Withdrawal amount</span><span>{naira(amt)}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Transaction fee</span><span data-testid="withdraw-fee">-{naira(d.withdrawal_fee)}</span></div>
          <div className="flex justify-between text-base font-bold"><span>You receive</span><span data-testid="withdraw-receive">{naira(receive)}</span></div>
        </div>
        <p className="text-xs text-muted-foreground">Requests are reviewed by our team and paid by bank transfer. Status updates appear in your wallet. Minimum {naira(d.min_withdrawal)}.</p>
        <Button type="submit" className="w-full" data-testid="withdraw-continue-btn">Continue</Button>
      </form>
      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm withdrawal</AlertDialogTitle>
            <AlertDialogDescription>Withdraw {naira(amt)} to {f.bank_name} ({f.account_number}) — {f.account_name}. You will receive {naira(receive)} after the {naira(d.withdrawal_fee)} fee.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={submit} data-testid="withdraw-confirm-btn">Confirm request</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export function SellerTransactions() {
  const [items, setItems] = useState(null);
  const [type, setType] = useState("");
  useEffect(() => { api.get("/seller/transactions", { params: { type } }).then((r) => setItems(r.data)); }, [type]);
  return (
    <div>
      <PageHeader eyebrow="Ledger" title="Transaction History" />
      <div className="mb-4 flex flex-wrap gap-2">
        {[["", "All"], ["sale", "Sales"], ["platform_fee", "Platform fees"], ["withdrawal", "Withdrawals"]].map(([v, l]) => (
          <button key={l} onClick={() => setType(v)} data-testid={`tx-filter-${l.toLowerCase().replace(" ", "-")}`} className={`rounded-full border px-3 py-1.5 text-xs font-medium ${type === v ? "border-primary bg-primary text-white" : "border-border"}`}>{l}</button>
        ))}
      </div>
      {!items ? <Spinner /> : <TxTable items={items} />}
    </div>
  );
}

export function SellerSales() {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/seller/sales").then((r) => setItems(r.data)); }, []);
  return (
    <div>
      <PageHeader eyebrow="Orders" title="Sales" />
      {!items ? <Spinner /> : items.length === 0 ? <EmptyState title="No sales yet" testid="sales-empty" /> : (
        <div className="card-surface overflow-x-auto">
          <Table>
            <TableHeader><TableRow><TableHead>Template</TableHead><TableHead>Buyer</TableHead><TableHead>Date</TableHead><TableHead className="text-right">Price</TableHead><TableHead className="text-right">Commission</TableHead><TableHead className="text-right">You earn</TableHead><TableHead>Settlement</TableHead></TableRow></TableHeader>
            <TableBody>
              {items.map((i) => (
                <TableRow key={i.id} data-testid={`sale-row-${i.id}`}>
                  <TableCell><div className="flex items-center gap-2"><img src={media(i.cover_image)} alt="" className="h-9 w-8 rounded object-cover" /><span className="max-w-[180px] truncate font-medium">{i.title}</span></div></TableCell>
                  <TableCell className="text-sm">{i.buyer_name}</TableCell>
                  <TableCell className="whitespace-nowrap text-xs">{fmtDate(i.paid_at)}</TableCell>
                  <TableCell className="text-right">{naira(i.price)}</TableCell>
                  <TableCell className="text-right text-rose-500">-{naira(i.commission_amount)}</TableCell>
                  <TableCell className="text-right font-semibold text-emerald-500">{naira(i.seller_earning)}</TableCell>
                  <TableCell><StatusBadge s={i.settled ? "completed" : "pending"} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
